import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** 외담대 발행(217) — V2 에서 새로 만든 양식(레거시 없음). 기획 PORTAL-10577 */
export const EXTERNAL_LOAN_TYPE = 217;

export const isExternalLoanType = (apvType: number): boolean =>
  apvType === EXTERNAL_LOAN_TYPE;

/** SAP `[외담대 결재상신]` 버튼 진입 — `issueDate=YYYYMMDD` */
export const EXTERNAL_LOAN_ENTRY_QUERY_KEY = 'issueDate';

export const EXTERNAL_LOAN_NOT_SEARCHED_MESSAGE =
  '구매카드 발급일을 선택하고 조회를 눌러주세요.';
export const EXTERNAL_LOAN_NOT_LOADED_MESSAGE =
  '외담대 발행내역 조회가 완료된 후 저장할 수 있습니다.';
export const EXTERNAL_LOAN_EMPTY_SAVE_MESSAGE =
  '발행 대상이 없어 저장할 수 없습니다.';
export const EXTERNAL_LOAN_LOAD_FAILED_MESSAGE =
  '외담대 발행내역을 불러올 수 없습니다.';

export type ExternalLoanItem = {
  sequence: string;
  bankName: string;
  /** YYYY-MM-DD */
  maturityDate: string;
  /** 하이픈 없음 */
  businessNumber: string;
  vendorName: string;
  /** 클레임 차감이 반영된 거래처별 집계라 음수일 수 있음 */
  amount: number;
};

/** 저장 attributes 그대로 — 조회 화면도 이 값으로 다시 그림 */
export type ExternalLoanData = {
  /** YYYY-MM-DD */
  issueDate: string;
  /** 발행 대상이 없으면 null */
  groupwareDocumentNumber: string | null;
  items: ExternalLoanItem[];
};

export type ExternalLoanDraftSource = {
  code: number;
  attributes: {
    issueDate?: string | null;
    groupwareDocumentNumber?: string | null;
    items?: unknown[] | null;
  };
  display?: { subject?: string };
};

export type ExternalLoanState = {
  /** 캘린더에서 고른 날짜(YYYY-MM-DD). 조회 전에는 화면 데이터와 다를 수 있음 */
  issueDate: string | null;
  loading: boolean;
  /** 조회 성공 결과(0건 포함). 조회 전·조회 중·실패는 null */
  data: ExternalLoanData | null;
  /** 조회 실패 안내 — SAP 문구 그대로, 그 밖은 EXTERNAL_LOAN_LOAD_FAILED_MESSAGE(번역 키) */
  error: string | null;
};

export const createExternalLoanState = (
  init: Partial<ExternalLoanState> = {},
): ExternalLoanState => ({
  issueDate: null,
  loading: false,
  data: null,
  error: null,
  ...init,
});

const pad2 = (n: number) => String(n).padStart(2, '0');

/** YYYYMMDD·YYYY-MM-DD → YYYY-MM-DD. 없는 날짜면 null */
export const toIssueDate = (value: unknown): string | null => {
  const match = /^(\d{4})-?(\d{2})-?(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  )
    return null;
  return `${match[1]}-${match[2]}-${match[3]}`;
};

export const getExternalLoanEntryIssueDate = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[EXTERNAL_LOAN_ENTRY_QUERY_KEY];
  return toIssueDate(Array.isArray(raw) ? raw[0] : raw) ?? undefined;
};

/** 「[OC] 2026.08월 외담대 발행」 — 연·월은 구매카드 발급일 기준 */
export const buildExternalLoanSubject = (
  issueDate: string,
  t: TFunction,
): string =>
  t('[OC] {{yearMonth}}월 외담대 발행', {
    yearMonth: issueDate.slice(0, 7).replace('-', '.'),
  });

export const formatExternalLoanAmount = (n: number): string =>
  n.toLocaleString('ko-KR');

/** 10자리면 XXX-XX-XXXXX, 아니면 그대로 */
export const formatBusinessNumber = (value: string): string =>
  /^\d{10}$/.test(value)
    ? `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`
    : value;

const LOCALES: Record<string, string> = {
  ko: 'ko-KR',
  en: 'en-US',
  zh: 'zh-CN',
};
const localeOf = (lang: string) => LOCALES[lang.slice(0, 2)] ?? 'ko-KR';

/** YYYY-MM-DD 가 아니면 null — SAP 값이 어긋나도 화면이 깨지지 않게 원문으로 표시 */
const parseDate = (value: string) => {
  if (toIssueDate(value) !== value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return { m, d, date: new Date(y, m - 1, d) };
};

const weekdayOf = (date: Date, lang: string) =>
  new Intl.DateTimeFormat(localeOf(lang), { weekday: 'short' }).format(date);

/** 2026-08-13 → 「2026년 8월 13일」(화면 언어) */
export const formatIssueDateLong = (value: string, lang: string): string => {
  const parsed = parseDate(value);
  return parsed
    ? new Intl.DateTimeFormat(localeOf(lang), { dateStyle: 'long' }).format(
        parsed.date,
      )
    : value;
};

/** 2026-08-14 → 「08.14 (금)」 */
export const formatMaturityShort = (value: string, lang: string): string => {
  const parsed = parseDate(value);
  return parsed
    ? `${pad2(parsed.m)}.${pad2(parsed.d)} (${weekdayOf(parsed.date, lang)})`
    : value;
};

/** 2026-08-14 → 「2026.08.14」 */
export const formatMaturityDate = (value: string): string =>
  value.replace(/-/g, '.');

/** 2026-08-14 → 「2026.08.14 (금)」 */
export const formatMaturityWithWeekday = (
  value: string,
  lang: string,
): string => {
  const parsed = parseDate(value);
  return parsed
    ? `${formatMaturityDate(value)} (${weekdayOf(parsed.date, lang)})`
    : value;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const toText = (v: unknown) => (v == null ? '' : String(v).trim());

const toItem = (row: unknown): ExternalLoanItem | null => {
  if (!isRecord(row)) return null;
  const amount = Number(row.amount);
  return {
    sequence: toText(row.sequence),
    bankName: toText(row.bankName),
    maturityDate: toIssueDate(row.maturityDate) ?? toText(row.maturityDate),
    businessNumber: toText(row.businessNumber).replace(/-/g, ''),
    vendorName: toText(row.vendorName),
    amount: Number.isFinite(amount) ? amount : 0,
  };
};

const toItems = (rows: unknown): ExternalLoanItem[] =>
  Array.isArray(rows)
    ? rows.map(toItem).filter((item): item is ExternalLoanItem => !!item)
    : [];

/** 조회 응답 → 저장값. 0건이면 결재묶음번호 null */
export const externalLoanDataFromDraftSource = (
  source: ExternalLoanDraftSource,
  issueDate: string,
): ExternalLoanData => {
  const items = toItems(source.attributes?.items);
  return {
    issueDate: toIssueDate(source.attributes?.issueDate) ?? issueDate,
    groupwareDocumentNumber: items.length
      ? toText(source.attributes?.groupwareDocumentNumber) || null
      : null,
    items,
  };
};

/** 저장 attributes. 발급일이 없으면 null */
export const parseExternalLoanData = (
  attributes: Record<string, unknown> | null | undefined,
): ExternalLoanData | null => {
  const issueDate = toIssueDate(attributes?.issueDate);
  if (!issueDate) return null;
  return {
    issueDate,
    groupwareDocumentNumber:
      toText(attributes?.groupwareDocumentNumber) || null,
    items: toItems(attributes?.items),
  };
};

/**
 * 편집 진입 — 저장 내역을 그대로 복원하고 다시 조회하지 않음.
 * SAP 는 조회할 때마다 결재묶음번호를 새로 발급해, 편집만 열어도 번호가 바뀌는 걸 막음
 */
export const externalLoanStateFromAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): ExternalLoanState => {
  const data = parseExternalLoanData(attributes);
  return createExternalLoanState({ issueDate: data?.issueDate ?? null, data });
};

/** 이탈 경고 판정 — 화면에 올라온 조회 결과(발급일·결재묶음번호)만 */
export const externalLoanKey = (
  state: ExternalLoanState | null | undefined,
): string =>
  state?.data
    ? JSON.stringify([state.data.issueDate, state.data.groupwareDocumentNumber])
    : '';

export const checkExternalLoanBeforeSave = (
  state: ExternalLoanState | null | undefined,
): string | null => {
  if (state?.loading || state?.error) return EXTERNAL_LOAN_NOT_LOADED_MESSAGE;
  if (!state?.data) return EXTERNAL_LOAN_NOT_SEARCHED_MESSAGE;
  if (!state.data.items.length || !state.data.groupwareDocumentNumber)
    return EXTERNAL_LOAN_EMPTY_SAVE_MESSAGE;
  return null;
};

export const buildExternalLoanAttributes = (
  state: ExternalLoanState | null | undefined,
): ApprovalAttributes | null => {
  const data = state?.data;
  if (!data?.groupwareDocumentNumber || !data.items.length) return null;
  return {
    issueDate: data.issueDate,
    groupwareDocumentNumber: data.groupwareDocumentNumber,
    items: data.items,
  };
};

// ===== 화면 집계 — 소계 순서: 지급은행 → 만기일 → 사업자번호 =====

/** 은행명 문자열 그대로 오름차순(KB국민은행 → 신한은행 → 우리은행) */
const compareText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

export type ExternalLoanMaturityGroup = {
  maturityDate: string;
  /** NO 는 은행 안에서 묶음을 넘어 이어짐 */
  rows: (ExternalLoanItem & { no: number })[];
  total: number;
};

export type ExternalLoanBank = {
  bankName: string;
  count: number;
  total: number;
  /** 만기일 → 합계. 그 은행에 없는 만기일은 키 없음 */
  byMaturity: Record<string, number>;
  groups: ExternalLoanMaturityGroup[];
};

export type ExternalLoanSummary = {
  count: number;
  total: number;
  maturityDates: string[];
  byMaturity: Record<string, number>;
  banks: ExternalLoanBank[];
};

export const summarizeExternalLoan = (
  items: ExternalLoanItem[],
): ExternalLoanSummary => {
  const sorted = [...items].sort(
    (a, b) =>
      compareText(a.bankName, b.bankName) ||
      compareText(a.maturityDate, b.maturityDate) ||
      compareText(a.businessNumber, b.businessNumber) ||
      compareText(a.sequence, b.sequence),
  );
  const banks: ExternalLoanBank[] = [];
  const byMaturity: Record<string, number> = {};
  sorted.forEach((item) => {
    let bank = banks[banks.length - 1];
    if (!bank || bank.bankName !== item.bankName) {
      bank = {
        bankName: item.bankName,
        count: 0,
        total: 0,
        byMaturity: {},
        groups: [],
      };
      banks.push(bank);
    }
    let group = bank.groups[bank.groups.length - 1];
    if (!group || group.maturityDate !== item.maturityDate) {
      group = { maturityDate: item.maturityDate, rows: [], total: 0 };
      bank.groups.push(group);
    }
    bank.count += 1;
    group.rows.push({ ...item, no: bank.count });
    group.total += item.amount;
    bank.total += item.amount;
    bank.byMaturity[item.maturityDate] =
      (bank.byMaturity[item.maturityDate] ?? 0) + item.amount;
    byMaturity[item.maturityDate] =
      (byMaturity[item.maturityDate] ?? 0) + item.amount;
  });
  return {
    count: sorted.length,
    total: banks.reduce((sum, bank) => sum + bank.total, 0),
    maturityDates: Object.keys(byMaturity).sort(compareText),
    byMaturity,
    banks,
  };
};
